<template>
  <div class="min-h-screen bg-gradient-to-b from-white to-light-purple/10 py-16 px-4 flex justify-center items-start">
    <div class="card-neumorphic max-w-4xl w-full shadow-xl">
      <div class="mb-6 border-b border-gray-200 pb-4">
        <h1 class="text-3xl font-poppins font-bold text-dark-gray">LinkedIn Privacy Policy</h1>
        <p class="text-sm text-gray-500 mt-2">Last updated: 27 October 2026</p>
      </div>

      <div class="prose prose-gray max-w-none">
        <p class="text-dark-gray/80 mb-8">
          This notice explains how Homvisit Health Private Limited ("Homvisit", "we") handles personal
          data received from LinkedIn when you authorize the Homvisit LinkedIn application. It covers
          that application only. Our
          <RouterLink to="/privacy-policy" class="text-light-purple hover:underline">website Privacy Policy</RouterLink>
          covers camp bookings, lab partnerships, and job applications on homvisit.com.
        </p>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">1. Who we are</h2>
          <p class="text-dark-gray/80">
            Homvisit Health Private Limited provides corporate health camps and phlebotomy services in
            Kerala, India, and operates https://homvisit.com. For LinkedIn member data received through
            our LinkedIn application, Homvisit is the organization that decides why and how that data is used.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">2. When this notice applies</h2>
          <p class="text-dark-gray/80">
            This notice applies when you sign in with LinkedIn, connect a LinkedIn account, or otherwise
            grant the Homvisit LinkedIn application permission to access your LinkedIn member data. We
            receive only the data you approve on LinkedIn's consent screen. We do not scrape LinkedIn,
            buy LinkedIn profile data, or access your LinkedIn account beyond the permissions you grant.
            We never receive your LinkedIn password.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">3. LinkedIn data we may receive</h2>
          <p class="text-dark-gray/80 mb-4">
            The fields depend on the permissions shown on LinkedIn's authorization screen. They can include:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Your name</li>
            <li>Your email address</li>
            <li>Your profile photo</li>
            <li>Your public profile URL and LinkedIn member identifier</li>
            <li>Your headline, current position, and company</li>
            <li>Any other profile field listed on the consent screen you approve</li>
          </ul>
          <p class="text-dark-gray/80 mt-4">
            We do not collect health or medical information through LinkedIn. Clinical details are
            collected only through Homvisit's own booking and care processes, under the website Privacy Policy.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">4. How we use LinkedIn data</h2>
          <p class="text-dark-gray/80 mb-4">We use LinkedIn member data to:</p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Confirm your identity and keep the connection you authorized</li>
            <li>Show your name and the profile details you approved inside Homvisit tools you use</li>
            <li>Reply to professional enquiries, partnership requests, and recruiting conversations you start with us</li>
            <li>Record the permissions you granted so we can honor a later revocation</li>
            <li>Protect the application against fraud, abuse, and unauthorized access</li>
            <li>Meet a legal or regulatory duty</li>
          </ul>
          <p class="text-dark-gray/80 mt-4">
            We do not sell LinkedIn member data. We do not use it to build advertising audiences, to
            train public AI models, or to make decisions about medical care. We do not pass it to labs,
            hospitals, or employers unless you separately give us that information through a Homvisit form.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">5. Who we share it with</h2>
          <p class="text-dark-gray/80 mb-4">We share LinkedIn member data only:</p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>With vendors who host or secure our systems, bound by contract, and only as needed to run the application</li>
            <li>With LinkedIn, where required to operate the integration you authorized</li>
            <li>With a court, regulator, or other authority when the law requires it</li>
            <li>With someone else when you ask us to</li>
          </ul>
          <p class="text-dark-gray/80 mt-4">
            We do not sell, rent, or trade LinkedIn member data.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">6. How long we keep it</h2>
          <p class="text-dark-gray/80">
            We keep LinkedIn member data only while a stated purpose still applies: while your
            authorization is active, while we need it to finish a request you made, or while the law
            requires a record. LinkedIn data is stored on access-controlled systems. When you revoke
            the application or ask us to delete the data, we delete or anonymize the LinkedIn data we
            hold within 30 days. We may keep a short record that we received and acted on your request,
            where the law requires that record.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">7. Your choices</h2>
          <p class="text-dark-gray/80 mb-4">
            You can withdraw the Homvisit application's access at any time in LinkedIn under
            Settings &amp; Privacy, then Data privacy, then Permitted services (or the current LinkedIn
            equivalent). Withdrawal stops further access. It does not by itself erase copies we already
            stored. Email us if you also want those copies deleted.
          </p>
          <p class="text-dark-gray/80">
            You may ask us to access, correct, or delete LinkedIn data we hold about you, or to withdraw
            consent. Email {{ contactEmail }} with the subject "LinkedIn data request". We will respond
            within 30 days.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">8. Children</h2>
          <p class="text-dark-gray/80">
            The Homvisit LinkedIn application is not directed at anyone under 18. We do not knowingly
            collect LinkedIn data from children. If you believe we have, contact us and we will delete it.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">9. Where data is processed</h2>
          <p class="text-dark-gray/80">
            Homvisit is based in Trivandrum, Kerala, India. LinkedIn member data we receive is processed
            in India. Where India's Digital Personal Data Protection Act, 2023 applies to that data, we
            handle it under this notice and that Act.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">10. Changes</h2>
          <p class="text-dark-gray/80">
            If we change this notice, we will update the date at the top of this page. The notice in
            effect when you authorize the application applies to data collected under that authorization.
            If a change affects how we use data we already hold, we will not apply the new use without
            a fresh authorization or another lawful basis.
          </p>
        </section>

        <section class="mb-8">
          <h2 class="text-xl font-semibold text-dark-gray mb-4">11. Contact</h2>
          <p class="text-dark-gray/80">
            Questions about this LinkedIn Privacy Policy, or a request to access or delete LinkedIn data:
          </p>
          <div class="mt-4 p-4 bg-light-purple/10 rounded-lg">
            <p class="text-dark-gray font-medium">Homvisit Health Private Limited</p>
            <p class="text-dark-gray font-medium">Email: {{ contactEmail }}</p>
            <p class="text-dark-gray font-medium">Phone: {{ contactPhone }}</p>
            <p class="text-dark-gray font-medium">Trivandrum, Kerala, India</p>
          </div>
        </section>

        <p class="text-sm text-gray-500">
          LinkedIn is a trademark of LinkedIn Corporation. Homvisit is not part of LinkedIn. This notice
          describes Homvisit's own handling of member data received through the LinkedIn application.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  breadcrumbSchema,
  graphSchema,
  organizationSchema,
  useSeo,
} from '../composables/useSeo'
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY } from '../config/contact'

export default {
  name: 'LinkedInPrivacyPolicy',
  setup() {
    useSeo({
      title: 'LinkedIn Privacy Policy | Homvisit Health Data Use',
      description:
        'How Homvisit Health Pvt Ltd collects, uses, stores, and deletes LinkedIn member data after you authorize the Homvisit LinkedIn app, and how to revoke access.',
      path: '/linkedin-privacy-policy',
      robots: 'index,follow',
      schema: graphSchema([
        organizationSchema(),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'LinkedIn Privacy Policy', path: '/linkedin-privacy-policy' },
        ]),
      ]),
    })

    return {
      contactEmail: CONTACT_EMAIL,
      contactPhone: CONTACT_PHONE_DISPLAY,
    }
  },
}
</script>
